import {
  priorityInboxApi,
  type PriorityInboxThreadsResponse,
  type UnreadCountResponse,
} from "@circle-react/api/priorityInboxApi";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import {
  isPriorityInboxKind,
  type PriorityInboxKind,
  type PriorityInboxStatus,
  type PriorityInboxThread,
} from "@circle-react/types/PriorityInboxThread";
import type { InfiniteData } from "react-query";
import { infiniteQueryOptions, queryOptions } from "react-query";

export const priorityInboxThreadKey = createQueryKeyFactory(
  "priority-inbox-thread",
);

const STALE_TIME = 60 * 1000;

export const priorityInboxThreadQueries = {
  list: ({
    status,
    kinds,
  }: {
    kinds?: PriorityInboxKind[];
    status?: PriorityInboxStatus;
  } = {}) =>
    infiniteQueryOptions({
      queryKey: priorityInboxThreadKey.key("list", {
        kinds: kinds ?? null,
        status: status ?? null,
      }),
      initialPageParam: 1,
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        priorityInboxApi.list({ page: pageParam, status, kinds }),
      getNextPageParam: (lastPage: PriorityInboxThreadsResponse) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
    }),
  unreadCount: () =>
    queryOptions({
      queryKey: priorityInboxThreadKey.key("unread-count"),
      queryFn: () => priorityInboxApi.unreadCount(),
      staleTime: STALE_TIME,
      refetchOnWindowFocus: true,
    }),
};

export const priorityInboxThreadRecords = (
  data: { pages: PriorityInboxThreadsResponse[] } | undefined,
): PriorityInboxThread[] => data?.pages.flatMap(page => page.records) ?? [];

export const priorityInboxThreadInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: priorityInboxThreadKey.root() }),
};

interface MarkReadSource {
  chatRoomUuid?: string;
  chatThreadId?: number;
  communityInboxChatId?: number;
  threadId?: number;
}

const matchesMarkedReadSource = (
  thread: PriorityInboxThread,
  source: MarkReadSource,
): boolean => {
  if (source.threadId !== undefined) {
    return thread.id === source.threadId;
  }
  if (
    source.chatRoomUuid &&
    thread.display_context.chat_room_uuid === source.chatRoomUuid
  ) {
    return true;
  }
  if (
    source.chatThreadId &&
    thread.display_context.chat_thread_id === source.chatThreadId
  ) {
    return true;
  }
  if (
    source.communityInboxChatId &&
    thread.kind === "ai" &&
    thread.groupable_id === source.communityInboxChatId
  ) {
    return true;
  }

  return false;
};

// Opening a thread marks its source read. Patch the cached row and count
// instead of refetching the paginated list.
const withThreadMarkedRead = (
  data: InfiniteData<PriorityInboxThreadsResponse> | undefined,
  source: MarkReadSource,
  readAt: string,
): {
  data: InfiniteData<PriorityInboxThreadsResponse> | undefined;
  newlyReadCount: number;
} => {
  if (!data) return { data, newlyReadCount: 0 };

  let newlyReadCount = 0;
  let didPageChange = false;
  const pages = data.pages.map(page => {
    let didRecordChange = false;
    const records = page.records.map(thread => {
      if (thread.read_at || !matchesMarkedReadSource(thread, source)) {
        return thread;
      }

      didRecordChange = true;
      newlyReadCount += 1;
      return { ...thread, read_at: readAt };
    });

    if (!didRecordChange) return page;

    didPageChange = true;
    return { ...page, records };
  });

  return {
    data: didPageChange ? { ...data, pages } : data,
    newlyReadCount,
  };
};

const listParams = (
  queryKey: readonly unknown[],
): { kinds?: PriorityInboxKind[]; status?: PriorityInboxStatus } => {
  const params = queryKey[2];
  if (!params || typeof params !== "object") return {};

  const status =
    "status" in params &&
    (params.status === "unsolved" || params.status === "solved")
      ? params.status
      : undefined;
  const kinds =
    "kinds" in params &&
    Array.isArray(params.kinds) &&
    params.kinds.every(
      (kind): kind is PriorityInboxKind =>
        typeof kind === "string" && isPriorityInboxKind(kind),
    )
      ? params.kinds
      : undefined;

  return { kinds, status };
};

const pagesWithoutSolvedThread = (
  current: InfiniteData<PriorityInboxThreadsResponse>,
  id: number,
): InfiniteData<PriorityInboxThreadsResponse> | undefined => {
  const records = current.pages.flatMap(page => page.records);
  if (!records.some(thread => thread.id === id)) return undefined;

  const remaining = records.filter(thread => thread.id !== id);
  const perPage = current.pages[0]?.per_page ?? remaining.length;
  const pages = current.pages.map((page, index) => ({
    ...page,
    records: remaining.slice(index * perPage, (index + 1) * perPage),
  }));

  return { ...current, pages };
};

// The list is offset-paginated, not cursor-paginated. Solving or unsolving
// removes a row, so the last loaded page must be refetched and reconciled or
// the next page fetch skips that item.
const reconcileRefetchedLastPage = (
  current: InfiniteData<PriorityInboxThreadsResponse>,
  refetched: PriorityInboxThreadsResponse,
): InfiniteData<PriorityInboxThreadsResponse> => {
  const loadedIds = new Set(
    current.pages
      .slice(0, -1)
      .flatMap(page => page.records)
      .map(thread => thread.id),
  );
  const lastPage = current.pages[current.pages.length - 1];
  if (!lastPage) return current;

  return {
    ...current,
    pages: [
      ...current.pages.slice(0, -1),
      {
        ...refetched,
        page: lastPage.page,
        records: refetched.records.filter(thread => !loadedIds.has(thread.id)),
      },
    ],
  };
};

// A status-scoped list no longer contains the row once it is solved or unsolved.
export const togglePriorityInboxThreadSolved = (id: number): void => {
  queryClient
    .getQueryCache()
    .findAll({ queryKey: priorityInboxThreadKey.key("list") })
    .forEach(query => {
      const current = queryClient.getQueryData<
        InfiniteData<PriorityInboxThreadsResponse>
      >(query.queryKey);
      if (!current) return;

      const next = pagesWithoutSolvedThread(current, id);
      if (!next) return;

      queryClient.setQueryData(query.queryKey, next);

      const lastPage = next.pages[next.pages.length - 1];
      const previousLastPage = current.pages[current.pages.length - 1];
      const didShortenLastPage =
        previousLastPage != null &&
        lastPage != null &&
        lastPage.records.length < previousLastPage.records.length;
      if (!didShortenLastPage || !lastPage.has_next_page) return;

      const { kinds, status } = listParams(query.queryKey);
      void priorityInboxApi
        .list({ kinds, page: lastPage.page, status })
        .then(refetched => {
          queryClient.setQueryData<InfiniteData<PriorityInboxThreadsResponse>>(
            query.queryKey,
            latest =>
              latest ? reconcileRefetchedLastPage(latest, refetched) : latest,
          );
        });
    });

  void queryClient.invalidateQueries({
    queryKey: priorityInboxThreadKey.key("unread-count"),
  });
};

export const markPriorityInboxThreadReadInCache = (
  source: MarkReadSource,
): void => {
  const readAt = new Date().toISOString();
  let newlyReadCount = 0;

  queryClient.setQueriesData<InfiniteData<PriorityInboxThreadsResponse>>(
    { queryKey: priorityInboxThreadKey.key("list") },
    current => {
      const updated = withThreadMarkedRead(current, source, readAt);
      newlyReadCount = Math.max(newlyReadCount, updated.newlyReadCount);
      return updated.data;
    },
  );

  if (newlyReadCount === 0) return;

  queryClient.setQueryData<UnreadCountResponse>(
    priorityInboxThreadKey.key("unread-count"),
    current =>
      current
        ? {
            unread_count: Math.max(0, current.unread_count - newlyReadCount),
          }
        : current,
  );
};
